import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "call-info-main" }
const _hoisted_2 = {
  key: 0,
  class: "call-info-loading-overlay",
  "aria-hidden": "true"
}
const _hoisted_3 = { class: "validity-icon valid" }
const _hoisted_4 = { class: "validity-icon invalid" }
const _hoisted_5 = { class: "validity-icon unverified" }
const _hoisted_6 = { class: "phone-number" }
const _hoisted_7 = { class: "call-type-label" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "ivr-group" }
const _hoisted_10 = ["onUpdate:modelValue"]
const _hoisted_11 = ["disabled", "aria-busy"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "gk-group" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled", "aria-busy"]
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "action-icons" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]
const _hoisted_20 = {
  key: 0,
  class: "details-line"
}
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["disabled"]

import { Icon } from "@iconify/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useEditContactPhoneNumber } from "@/composables/contacts/useEditContactPhoneNumber";
import { useUpsertContactNumberInfo } from "@/composables/contacts/useUpsertContactNumberInfo";
import { useToast } from "@/composables/ui/useToast";

export interface CallInfoRowProps {
  phoneNumber: string;
  callType?: CallType;
  validity?: Validity;
  ivrNumber?: string;
  gateKeeperName?: string;
  callStatus?: CallStatus;
  isCurrentCall?: boolean;
  callSid?: string;
}

export type Validity = "Valid" | "Invalid" | "Unverified";
export type CallType = "Mobile" | "Direct" | "IVR" | "Gate Keeper";
export type CallStatus =
  | "ready"
  | "connecting"
  | "ringing"
  | "in-call"
  | "ended"
  | "error"
  | "reconnecting";

const IVR_DIGIT_COUNT = 3;
const DEBOUNCE_MS = 500;
const DROPDOWN_POSITION_DELAY_MS = 10;

type DropdownKind = "validity" | "type" | "actions";


export default /*@__PURE__*/_defineComponent({
  __name: 'CallInfoRow',
  props: {
    phoneNumber: {},
    contactId: {},
    callType: { default: "Mobile" },
    validity: { default: "Unverified" },
    ivrNumber: { default: "" },
    gateKeeperName: { default: "" },
    callStatus: { default: "ready" },
    isCurrentCall: { type: Boolean, default: false },
    callSid: { type: [String, Boolean], default: "" }
  },
  emits: ["change", "phone-click", "menu-click", "edit-click", "delete-click", "data-updated"],
  setup(__props: any, { emit: __emit }) {

const VALIDITY_OPTIONS: Validity[] = ["Valid", "Invalid", "Unverified"];
const CALL_TYPES: CallType[] = ["Mobile", "Direct", "IVR", "Gate Keeper"];
const CALL_STATUSES_IN_CALL: CallStatus[] = ["connecting", "ringing", "in-call", "reconnecting"];
const props = __props;

const emit = __emit;

const toast = useToast();
const { editContactPhoneNumber, isEditing } = useEditContactPhoneNumber();
const { upsertNumberInfo, isUpserting } = useUpsertContactNumberInfo();

const isUpdating = computed(() => isEditing.value || isUpserting.value);

function getValidityClass(validity: Validity): string {
  if (validity === "Valid") return "valid";
  if (validity === "Invalid") return "invalid";
  return "unverified";
}

function toE164(value: string): string {
  const trimmed = (value ?? "").toString().trim();
  let normalized = trimmed.replace(/[^0-9+]/g, "");
  if (!normalized.startsWith("+") && normalized.startsWith("00")) {
    normalized = "+" + normalized.slice(2);
  }
  return normalized;
}

const internalValidity = ref<Validity>(
  props.validity && VALIDITY_OPTIONS.includes(props.validity) ? props.validity : "Unverified"
);
const internalCallType = ref<CallType>(
  props.callType && CALL_TYPES.includes(props.callType) ? props.callType : "Mobile"
);
const internalIvrNumber = ref(props.ivrNumber);
const internalIvrParts = ref<string[]>(props.ivrNumber ? props.ivrNumber.split("-") : ["", "", ""]);
const internalGateKeeperName = ref(props.gateKeeperName);

const showIVRInput = ref(false);
const showGateKeeperInput = ref(false);

const isValidityOpen = ref(false);
const isCallTypeOpen = ref(false);
const isActionsOpen = ref(false);

const validityDropdown = ref<HTMLElement | null>(null);
const callTypeDropdown = ref<HTMLElement | null>(null);
const validityDropdownMenu = ref<HTMLElement | null>(null);
const callTypeDropdownMenu = ref<HTMLElement | null>(null);
const actionsDropdown = ref<HTMLElement | null>(null);
const actionsDropdownMenu = ref<HTMLElement | null>(null);
const ivrInputRefs = ref<unknown[]>([]);
const gateKeeperInputRef = ref<HTMLInputElement | null>(null);

const validityDropdownStyle = ref<Record<string, string>>({});
const callTypeDropdownStyle = ref<Record<string, string>>({});
const actionsDropdownStyle = ref<Record<string, string>>({});

function updateDropdownPosition(
  refEl: HTMLElement | null,
  kind: DropdownKind
): Record<string, string> {
  if (!refEl) return {};
  const rect = refEl.getBoundingClientRect();
  const spacing = 4;

  if (kind === "actions") {
    const menuWidth = 160;
    const top = rect.bottom + spacing;
    const left = rect.right - menuWidth;
    return {
      position: "fixed",
      top: `${top}px`,
      left: `${Math.max(8, left)}px`,
      zIndex: "99999",
    };
  }

  const approxWidth = kind === "validity" ? 180 : 160;
  const approxHeight = kind === "validity" ? 150 : 180;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const showAbove = spaceBelow < approxHeight + 20 && spaceAbove > spaceBelow;
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - approxWidth - 8);
  const top = showAbove
    ? Math.max(8, rect.top - approxHeight - spacing)
    : Math.min(rect.bottom + spacing, window.innerHeight - approxHeight - 8);
  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    zIndex: "99999",
    minWidth: `${Math.max(rect.width, 120)}px`,
    maxHeight: `${Math.min(approxHeight, window.innerHeight - 16)}px`,
    overflowY: "auto",
  };
}

watch(
  () => props.validity,
  (newVal) => {
    if (newVal && VALIDITY_OPTIONS.includes(newVal)) {
      internalValidity.value = newVal;
    }
  }
);

watch(
  () => props.callType,
  (newVal) => {
    internalCallType.value = newVal && CALL_TYPES.includes(newVal) ? newVal : "Mobile";
    showIVRInput.value = false;
    showGateKeeperInput.value = false;
  }
);

watch(
  () => props.ivrNumber,
  (newVal) => {
    const raw = newVal ?? "";
    internalIvrNumber.value = raw;
    const parts = raw.split("-");
    internalIvrParts.value = [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
    showIVRInput.value = false;
  }
);

watch(
  () => props.gateKeeperName,
  (newVal) => {
    internalGateKeeperName.value = newVal ?? "";
    showGateKeeperInput.value = false;
  }
);

const saveDebounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);

function scheduleEmitChange() {
  if (saveDebounceTimer.value) clearTimeout(saveDebounceTimer.value);
  saveDebounceTimer.value = setTimeout(() => {
    emitChange();
  }, DEBOUNCE_MS);
}

function toggleValidityDropdown() {
  isValidityOpen.value = !isValidityOpen.value;
  if (isValidityOpen.value) {
    isCallTypeOpen.value = false;
    setTimeout(() => {
      validityDropdownStyle.value = updateDropdownPosition(validityDropdown.value, "validity");
    }, DROPDOWN_POSITION_DELAY_MS);
  }
}

function toggleCallTypeDropdown() {
  isCallTypeOpen.value = !isCallTypeOpen.value;
  if (isCallTypeOpen.value) {
    isValidityOpen.value = false;
    setTimeout(() => {
      callTypeDropdownStyle.value = updateDropdownPosition(callTypeDropdown.value, "type");
    }, DROPDOWN_POSITION_DELAY_MS);
  }
}

function toggleActionsDropdown() {
  isActionsOpen.value = !isActionsOpen.value;
  if (isActionsOpen.value) {
    isValidityOpen.value = false;
    isCallTypeOpen.value = false;
    setTimeout(() => {
      actionsDropdownStyle.value = updateDropdownPosition(actionsDropdown.value, "actions");
    }, DROPDOWN_POSITION_DELAY_MS);
  }
}

async function patchEditNumber(body: {
  phoneNumberType?: "MOBILE" | "DIRECT";
  routeType?: "DIRECT" | "MOBILE" | "IVR" | "GATEKEEPER";
  ivrExtension?: string;
  gatekeeperName?: string;
  isValid?: boolean | null;
}): Promise<boolean> {
  try {
    await editContactPhoneNumber({
      contactId: props.contactId,
      oldPhoneNumber: toE164(props.phoneNumber),
      phoneNumber: toE164(props.phoneNumber),
      ...body,
    });
    toast?.success("Saved");
    emit("data-updated");
    return true;
  } catch {
    return false;
  }
}

async function doUpsertNumberInfo(body: {
  routeType?: "DIRECT" | "MOBILE" | "IVR" | "GATEKEEPER";
  ivrDigits?: string;
  gatekeeperName?: string;
  isValid?: boolean | null;
}): Promise<void> {
  try {
    await upsertNumberInfo({
      contactId: props.contactId,
      phoneNumber: toE164(props.phoneNumber),
      ...body,
    });
    toast?.success("Saved");
    emit("data-updated");
  } catch {
    toast?.error("Failed to save");
  }
}

function selectValidity(validity: Validity) {
  internalValidity.value = validity;
  isValidityOpen.value = false;
  emitChange();
  const isValidLead = validity === "Valid" ? true : validity === "Invalid" ? false : null;
  if (isValidLead === null) {
    patchEditNumber({ isValid: null }).then((ok) => {
      if (!ok) void doUpsertNumberInfo({ isValid: null });
    });
  } else {
    patchEditNumber({ isValid: isValidLead }).then((ok) => {
      if (!ok) void doUpsertNumberInfo({ isValid: isValidLead });
    });
  }
}

const isInCall = computed(() => CALL_STATUSES_IN_CALL.includes(props.callStatus));

function openEdit() {
  if (internalCallType.value === "IVR") {
    showIVRInput.value = true;
    showGateKeeperInput.value = false;
    nextTick(() => (ivrInputRefs.value?.[0] as HTMLInputElement | undefined)?.focus());
  } else if (internalCallType.value === "Gate Keeper") {
    showGateKeeperInput.value = true;
    showIVRInput.value = false;
    nextTick(() => gateKeeperInputRef.value?.focus());
  }
}

watch(
  () => internalIvrNumber.value,
  () => {
    if (internalCallType.value === "IVR") scheduleEmitChange();
  }
);

watch(
  () => internalGateKeeperName.value,
  () => {
    if (internalCallType.value === "Gate Keeper") scheduleEmitChange();
  }
);

function selectCallType(type: CallType) {
  internalCallType.value = type;
  isCallTypeOpen.value = false;

  if (type === "IVR") {
    showIVRInput.value = true;
    showGateKeeperInput.value = false;
    internalGateKeeperName.value = "";
    nextTick(() => (ivrInputRefs.value?.[0] as HTMLInputElement | undefined)?.focus());
  } else if (type === "Gate Keeper") {
    showGateKeeperInput.value = true;
    showIVRInput.value = false;
    internalIvrNumber.value = "";
    internalIvrParts.value = ["", "", ""];
    nextTick(() => gateKeeperInputRef.value?.focus());
  } else {
    showIVRInput.value = false;
    showGateKeeperInput.value = false;
    internalIvrNumber.value = "";
    internalIvrParts.value = ["", "", ""];
    internalGateKeeperName.value = "";
    if (type === "Mobile" || type === "Direct") {
      const phoneNumberType = type === "Mobile" ? "MOBILE" : "DIRECT";
      patchEditNumber({ phoneNumberType }).then((ok) => {
        if (!ok) void doUpsertNumberInfo({ routeType: phoneNumberType as "MOBILE" | "DIRECT" });
      });
    }
  }

  emitChange();
}

function closeOtherEditors(target: "IVR" | "Gate Keeper") {
  if (target === "IVR") showGateKeeperInput.value = false;
  else showIVRInput.value = false;
}

const onIvrFocus = () => closeOtherEditors("IVR");
const onGateKeeperFocus = () => closeOtherEditors("Gate Keeper");

function emitChange() {
  emit("change", {
    phoneNumber: props.phoneNumber,
    callType: internalCallType.value,
    validity: internalValidity.value,
    ivrNumber: internalIvrNumber.value,
    gateKeeperName: internalGateKeeperName.value,
  });
}

async function updateIVRNumbers(ivrNumbers: string[]) {
  const digits = (ivrNumbers.join("") ?? "").replace(/[^0-9]/g, "");
  if (digits.length !== IVR_DIGIT_COUNT) {
    toast?.error(`IVR must be exactly ${IVR_DIGIT_COUNT} digits`);
    return;
  }
  const ok = await patchEditNumber({ routeType: "IVR", ivrExtension: digits });
  if (!ok) await doUpsertNumberInfo({ routeType: "IVR", ivrDigits: digits });
}

async function updateGateKeeperName(gateKeeperName: string) {
  const ok = await patchEditNumber({ routeType: "GATEKEEPER", gatekeeperName: gateKeeperName });
  if (!ok) await doUpsertNumberInfo({ routeType: "GATEKEEPER", gatekeeperName: gateKeeperName });
}

function saveIVR() {
  const parts = internalIvrParts.value.map((p) => (p ?? "").replace(/[^0-9*#+]/g, ""));
  internalIvrParts.value = [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
  const joined = internalIvrParts.value.filter((p) => p?.length).join("-");
  internalIvrNumber.value = joined;
  emitChange();
  showIVRInput.value = false;
  const ivrNumbers = internalIvrParts.value.filter((p) => p?.length);
  updateIVRNumbers(ivrNumbers);
}

function deleteIVR() {
  internalIvrNumber.value = "";
  internalIvrParts.value = ["", "", ""];
  emitChange();
  showIVRInput.value = false;
}

function saveGateKeeper() {
  internalGateKeeperName.value = (internalGateKeeperName.value ?? "").trim();
  emitChange();
  showGateKeeperInput.value = false;
  updateGateKeeperName(internalGateKeeperName.value);
}

function deleteGateKeeper() {
  internalGateKeeperName.value = "";
  emitChange();
  showGateKeeperInput.value = false;
}

function handlePhoneClick() {
  emit("phone-click", props.phoneNumber);
}

function onMenuButtonClick() {
  toggleActionsDropdown();
  emit("menu-click", props.phoneNumber);
}

function emitEdit() {
  const rect = actionsDropdown.value?.getBoundingClientRect() ?? null;
  isActionsOpen.value = false;
  emit("edit-click", props.phoneNumber, rect);
}

function emitDelete() {
  isActionsOpen.value = false;
  emit("delete-click", props.phoneNumber);
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node;
  if (
    validityDropdown.value &&
    !validityDropdown.value.contains(target) &&
    (!validityDropdownMenu.value || !validityDropdownMenu.value.contains(target))
  ) {
    isValidityOpen.value = false;
  }
  if (
    callTypeDropdown.value &&
    !callTypeDropdown.value.contains(target) &&
    (!callTypeDropdownMenu.value || !callTypeDropdownMenu.value.contains(target))
  ) {
    isCallTypeOpen.value = false;
  }
  if (
    actionsDropdown.value &&
    !actionsDropdown.value.contains(target) &&
    (!actionsDropdownMenu.value || !actionsDropdownMenu.value.contains(target))
  ) {
    isActionsOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  if (saveDebounceTimer.value) clearTimeout(saveDebounceTimer.value);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["call-info-row", { 'is-loading': isUpdating.value }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (isUpdating.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_unref(Icon), {
              icon: "svg-spinners:ring-resize",
              width: "20",
              height: "20",
              class: "loading-spinner"
            }),
            _cache[12] || (_cache[12] = _createElementVNode("span", { class: "loading-label" }, "Saving…", -1))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: "validity-dropdown",
        ref_key: "validityDropdown",
        ref: validityDropdown
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["validity-selector", { 'is-open': isValidityOpen.value, 'is-disabled': isUpdating.value }]),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (!isUpdating.value && toggleValidityDropdown()))
        }, [
          _createElementVNode("span", {
            class: _normalizeClass(["validity-icon", getValidityClass(internalValidity.value || 'Unverified')])
          }, [
            (internalValidity.value === 'Valid')
              ? (_openBlock(), _createBlock(_unref(Icon), {
                  key: 0,
                  icon: "mdi:check",
                  width: "16",
                  height: "16"
                }))
              : (internalValidity.value === 'Invalid')
                ? (_openBlock(), _createBlock(_unref(Icon), {
                    key: 1,
                    icon: "mdi:close",
                    width: "16",
                    height: "16"
                  }))
                : (_openBlock(), _createBlock(_unref(Icon), {
                    key: 2,
                    icon: "mdi:help",
                    width: "16",
                    height: "16"
                  }))
          ], 2),
          _createVNode(_unref(Icon), {
            icon: "mdi:chevron-down",
            width: "14",
            height: "14",
            class: _normalizeClass(["validity-dropdown-icon", { rotated: isValidityOpen.value }])
          }, null, 8, ["class"])
        ], 2),
        (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
          _createVNode(_Transition, { name: "dropdown-fade" }, {
            default: _withCtx(() => [
              (isValidityOpen.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    ref_key: "validityDropdownMenu",
                    ref: validityDropdownMenu,
                    class: "validity-dropdown-menu",
                    style: _normalizeStyle(validityDropdownStyle.value)
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["validity-option", { selected: internalValidity.value === 'Valid' }]),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (selectValidity('Valid')))
                    }, [
                      _createElementVNode("span", _hoisted_3, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:check",
                          width: "14",
                          height: "14"
                        })
                      ]),
                      _cache[13] || (_cache[13] = _createElementVNode("span", { class: "validity-label" }, "Valid", -1))
                    ], 2),
                    _createElementVNode("div", {
                      class: _normalizeClass(["validity-option", { selected: internalValidity.value === 'Invalid' }]),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (selectValidity('Invalid')))
                    }, [
                      _createElementVNode("span", _hoisted_4, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:close",
                          width: "14",
                          height: "14"
                        })
                      ]),
                      _cache[14] || (_cache[14] = _createElementVNode("span", { class: "validity-label" }, "Invalid", -1))
                    ], 2),
                    _createElementVNode("div", {
                      class: _normalizeClass(["validity-option", { selected: internalValidity.value === 'Unverified' }]),
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (selectValidity('Unverified')))
                    }, [
                      _createElementVNode("span", _hoisted_5, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:help",
                          width: "14",
                          height: "14"
                        })
                      ]),
                      _cache[15] || (_cache[15] = _createElementVNode("span", { class: "validity-label" }, "Unverified", -1))
                    ], 2)
                  ], 4))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]))
      ], 512),
      _createElementVNode("div", _hoisted_6, _toDisplayString(__props.phoneNumber), 1),
      _createElementVNode("div", {
        class: "call-type-dropdown",
        ref_key: "callTypeDropdown",
        ref: callTypeDropdown,
        style: {"position":"relative"}
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["call-type-selector", { 'is-open': isCallTypeOpen.value, 'is-disabled': isUpdating.value }]),
          onClick: _cache[4] || (_cache[4] = ($event: any) => (!isUpdating.value && toggleCallTypeDropdown()))
        }, [
          _createElementVNode("span", _hoisted_7, _toDisplayString(internalCallType.value), 1),
          _createVNode(_unref(Icon), {
            icon: "mdi:chevron-down",
            width: "12",
            height: "12",
            class: _normalizeClass(["dropdown-icon", { rotated: isCallTypeOpen.value }])
          }, null, 8, ["class"])
        ], 2),
        (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
          _createVNode(_Transition, { name: "dropdown-fade" }, {
            default: _withCtx(() => [
              (isCallTypeOpen.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    ref_key: "callTypeDropdownMenu",
                    ref: callTypeDropdownMenu,
                    class: "call-type-dropdown-menu",
                    style: _normalizeStyle(callTypeDropdownStyle.value)
                  }, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(CALL_TYPES, (type) => {
                      return _createElementVNode("div", {
                        key: type,
                        class: _normalizeClass(["call-type-option", { selected: internalCallType.value === type }]),
                        onClick: ($event: any) => (selectCallType(type))
                      }, _toDisplayString(type), 11, _hoisted_8)
                    }), 64))
                  ], 4))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ])),
        (showIVRInput.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "detail-popover",
              onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"])),
              onMousedown: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createElementVNode("button", {
                class: "detail-popover-close",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (showIVRInput.value = false)),
                type: "button"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:close",
                  width: "12",
                  height: "12"
                })
              ]),
              _cache[16] || (_cache[16] = _createElementVNode("label", { class: "input-label" }, "IVR Numbers", -1)),
              _createElementVNode("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(internalIvrParts.value, (part, idx) => {
                  return _withDirectives((_openBlock(), _createElementBlock("input", {
                    key: idx,
                    ref_for: true,
                    ref_key: "ivrInputRefs",
                    ref: ivrInputRefs,
                    "onUpdate:modelValue": ($event: any) => ((internalIvrParts.value[idx]) = $event),
                    type: "text",
                    class: "input-field ivr-field",
                    onKeyup: _withKeys(saveIVR, ["enter"]),
                    onFocus: onIvrFocus
                  }, null, 40, _hoisted_10)), [
                    [_vModelText, internalIvrParts.value[idx]]
                  ])
                }), 128)),
                _createElementVNode("button", {
                  class: "icon-action save",
                  type: "button",
                  disabled: isUpdating.value,
                  "aria-busy": isUpdating.value,
                  onClick: saveIVR,
                  title: "Save"
                }, [
                  (isUpdating.value)
                    ? (_openBlock(), _createBlock(_unref(Icon), {
                        key: 0,
                        icon: "svg-spinners:ring-resize",
                        width: "14",
                        height: "14"
                      }))
                    : (_openBlock(), _createBlock(_unref(Icon), {
                        key: 1,
                        icon: "mdi:check",
                        width: "14",
                        height: "14"
                      }))
                ], 8, _hoisted_11),
                _createElementVNode("button", {
                  class: "icon-action delete",
                  type: "button",
                  disabled: isUpdating.value,
                  onClick: deleteIVR,
                  title: "Delete"
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:delete-outline",
                    width: "14",
                    height: "14"
                  })
                ], 8, _hoisted_12)
              ])
            ], 32))
          : _createCommentVNode("", true),
        (showGateKeeperInput.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: "detail-popover",
              onClick: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["stop"])),
              onMousedown: _cache[11] || (_cache[11] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createElementVNode("button", {
                class: "detail-popover-close",
                onClick: _cache[8] || (_cache[8] = ($event: any) => (showGateKeeperInput.value = false)),
                type: "button"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:close",
                  width: "12",
                  height: "12"
                })
              ]),
              _cache[17] || (_cache[17] = _createElementVNode("label", { class: "input-label" }, "Gate Keeper Name", -1)),
              _createElementVNode("div", _hoisted_13, [
                _withDirectives(_createElementVNode("input", {
                  ref_key: "gateKeeperInputRef",
                  ref: gateKeeperInputRef,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((internalGateKeeperName).value = $event)),
                  type: "text",
                  class: "input-field gk-field",
                  disabled: isUpdating.value,
                  onKeyup: _withKeys(saveGateKeeper, ["enter"]),
                  onFocus: onGateKeeperFocus
                }, null, 40, _hoisted_14), [
                  [_vModelText, internalGateKeeperName.value]
                ]),
                _createElementVNode("button", {
                  class: "icon-action save",
                  type: "button",
                  disabled: isUpdating.value,
                  "aria-busy": isUpdating.value,
                  onClick: saveGateKeeper,
                  title: "Save"
                }, [
                  (isUpdating.value)
                    ? (_openBlock(), _createBlock(_unref(Icon), {
                        key: 0,
                        icon: "svg-spinners:ring-resize",
                        width: "14",
                        height: "14"
                      }))
                    : (_openBlock(), _createBlock(_unref(Icon), {
                        key: 1,
                        icon: "mdi:check",
                        width: "14",
                        height: "14"
                      }))
                ], 8, _hoisted_15),
                _createElementVNode("button", {
                  class: "icon-action delete",
                  type: "button",
                  disabled: isUpdating.value,
                  onClick: deleteGateKeeper,
                  title: "Delete"
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:delete-outline",
                    width: "14",
                    height: "14"
                  })
                ], 8, _hoisted_16)
              ])
            ], 32))
          : _createCommentVNode("", true)
      ], 512),
      _createElementVNode("div", _hoisted_17, [
        _createElementVNode("button", {
          class: _normalizeClass(["icon-btn phone-btn", { 'active-call-btn': isInCall.value && __props.isCurrentCall }]),
          type: "button",
          disabled: isUpdating.value,
          onClick: handlePhoneClick
        }, [
          _createVNode(_unref(Icon), {
            icon: isInCall.value && __props.isCurrentCall ? 'mdi:phone-hangup' : 'mdi:phone',
            width: "16",
            height: "16"
          }, null, 8, ["icon"])
        ], 10, _hoisted_18),
        _createElementVNode("button", {
          class: "icon-btn menu-btn",
          type: "button",
          ref_key: "actionsDropdown",
          ref: actionsDropdown,
          disabled: isUpdating.value,
          onClick: onMenuButtonClick
        }, [
          _createVNode(_unref(Icon), {
            icon: "mdi:dots-vertical",
            width: "16",
            height: "16"
          })
        ], 8, _hoisted_19)
      ])
    ]),
    _createVNode(_Transition, { name: "slide-fade" }, {
      default: _withCtx(() => [
        (internalCallType.value === 'IVR' || internalCallType.value === 'Gate Keeper')
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              (internalCallType.value === 'IVR')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[18] || (_cache[18] = _createElementVNode("span", { class: "details-label" }, "IVR:", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["details-value", { 'details-empty': !internalIvrNumber.value }])
                    }, _toDisplayString(internalIvrNumber.value ? "Press " + internalIvrNumber.value : "-"), 3),
                    _createElementVNode("button", {
                      class: "icon-action",
                      type: "button",
                      disabled: isUpdating.value,
                      title: "Edit",
                      onClick: openEdit
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:pencil-outline",
                        width: "14",
                        height: "14"
                      })
                    ], 8, _hoisted_21)
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _cache[19] || (_cache[19] = _createElementVNode("span", { class: "details-label" }, "Gate Keeper:", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["details-value", { 'details-empty': !internalGateKeeperName.value }])
                    }, _toDisplayString(internalGateKeeperName.value || "-"), 3),
                    _createElementVNode("button", {
                      class: "icon-action",
                      type: "button",
                      disabled: isUpdating.value,
                      title: "Edit",
                      onClick: openEdit
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:pencil-outline",
                        width: "14",
                        height: "14"
                      })
                    ], 8, _hoisted_22)
                  ], 64))
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "dropdown-fade" }, {
        default: _withCtx(() => [
          (isActionsOpen.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "actionsDropdownMenu",
                ref: actionsDropdownMenu,
                class: "actions-dropdown-menu",
                style: _normalizeStyle(actionsDropdownStyle.value)
              }, [
                _createElementVNode("div", {
                  class: "actions-item",
                  onClick: emitEdit
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:pencil-outline",
                    width: "16",
                    height: "16"
                  }),
                  _cache[20] || (_cache[20] = _createElementVNode("span", null, "Edit", -1))
                ]),
                _createElementVNode("div", {
                  class: "actions-item danger",
                  onClick: emitDelete
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:delete-outline",
                    width: "16",
                    height: "16"
                  }),
                  _cache[21] || (_cache[21] = _createElementVNode("span", null, "Delete", -1))
                ])
              ], 4))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ], 2))
}
}

})